import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ChevronRight, Loader2, RotateCcw } from 'lucide-react';
import { ShopChrome, Crumbs, SignInPrompt } from '@/components/shop/ShopChrome';
import { StatusPill } from '@/components/shop/ShopUI';
import { EmptyState, Img, Shimmer } from '@/components/ui';
import { cancelReturn, getReturns } from '@/api/shop';
import { useAuth } from '@/context/AuthContext';
import { friendlyError } from '@/lib/apiError';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import { RETURN_LABELS, RETURN_REASONS, istDate, rupees } from '@/lib/shop';

const returnTone = (s) =>
  s === 'refunded' ? 'success' : s === 'rejected' ? 'danger' : s === 'cancelled' ? 'muted' : s === 'requested' ? 'warning' : 'info';

const reasonLabel = (v) => RETURN_REASONS.find((r) => r.value === v)?.label || v;

/** Every return the customer has asked for, newest first (GET /shop/returns). */
const ShopReturns = () => {
  const { user, openAuthModal } = useAuth();
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  useDocumentMeta({ title: 'My returns — LK Shop', description: 'Track the items you sent back and their refunds.' });

  const load = useCallback(() => {
    setError(null);
    return getReturns()
      .then((rows) => setList(rows || []))
      .catch((err) => setError(friendlyError(err, 'Your returns could not be loaded')));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const undo = async (r) => {
    setCancelling(r.id);
    try {
      await cancelReturn(r.id);
      toast.success('Return cancelled');
      await load();
    } catch (err) {
      toast.error(friendlyError(err, 'Could not cancel the return'));
    } finally {
      setCancelling(null);
    }
  };

  if (!user) {
    return (
      <ShopChrome>
        <SignInPrompt icon={RotateCcw} title="Your returns" message="Log in to track returns and refunds." onLogin={openAuthModal} />
      </ShopChrome>
    );
  }

  return (
    <ShopChrome>
      <div className="lk-container max-w-4xl py-5 sm:py-8">
        <Crumbs items={[{ name: 'Shop', to: '/shop' }, { name: 'My orders', to: '/shop/orders' }, { name: 'Returns' }]} />
        <h1 className="mt-3 text-h2 text-ink-900">My returns</h1>
        <p className="mt-1 text-sm text-ink-500">Refunds go back to your original payment method in 5–7 working days after the seller receives the item.</p>

        <div className="mt-5 space-y-3">
          {error ? (
            <EmptyState
              icon={RotateCcw}
              title="Something went wrong"
              message={error}
              action={
                <button type="button" onClick={load} className="lk-btn-primary">
                  Try again
                </button>
              }
            />
          ) : !list ? (
            Array.from({ length: 3 }, (_, i) => <Shimmer key={i} className="h-28 w-full rounded-2xl" />)
          ) : list.length === 0 ? (
            <EmptyState
              icon={RotateCcw}
              title="No returns"
              message="Returns you request from an order show up here."
              action={<Link to="/shop/orders" className="lk-btn-primary">My orders</Link>}
            />
          ) : (
            list.map((r) => {
              const item = r.item || {};
              const refunded = r.status === 'refunded' && Number(r.refundAmount) > 0;
              const expected = Number(r.refundAmount) > 0 ? Number(r.refundAmount) : Number(item.unitPrice || 0) * r.qty;
              const open = !['refunded', 'rejected', 'cancelled'].includes(r.status);
              return (
                <article key={r.id} className="lk-card flex gap-4 p-4 sm:p-5">
                  <Img src={item.image} alt={item.title || ''} className="h-24 w-[72px] shrink-0 rounded-xl" />
                  <div className="min-w-0 flex-1 text-sm">
                    <StatusPill tone={returnTone(r.status)}>{RETURN_LABELS[r.status] || r.status}</StatusPill>
                    <p className="mt-1.5 line-clamp-2 font-medium text-ink-900">{item.title || 'Item'}</p>
                    <p className="text-xs text-ink-500">
                      {[item.variantLabel, `Qty ${r.qty}`].filter(Boolean).join(' · ')} · {reasonLabel(r.reason)}
                    </p>
                    <p className="tabular mt-1 text-xs text-ink-500">
                      Requested {istDate(r.createdAt)}
                      {r.resolvedAt && !open ? ` · closed ${istDate(r.resolvedAt)}` : ''}
                    </p>
                    {r.status !== 'rejected' && r.status !== 'cancelled' && expected > 0 && (
                      <p className={`tabular mt-1 font-semibold ${refunded ? 'text-success' : 'text-ink-900'}`}>
                        {refunded ? `${rupees(r.refundAmount)} refunded` : `Refund ${rupees(expected)} once the seller receives it`}
                      </p>
                    )}
                    {r.resolutionNote && <p className="mt-1 text-xs text-ink-700">Seller: {r.resolutionNote}</p>}
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                      <Link to={`/shop/orders/${r.orderId}`} className="lk-focus inline-flex items-center gap-0.5 rounded text-xs font-semibold text-brand-secondary hover:underline">
                        View order <ChevronRight className="h-3.5 w-3.5" />
                      </Link>
                      {r.status === 'requested' && (
                        <button
                          type="button"
                          onClick={() => undo(r)}
                          disabled={cancelling === r.id}
                          className="lk-focus inline-flex min-h-[32px] items-center gap-1 rounded text-xs font-semibold text-danger hover:underline disabled:opacity-60"
                        >
                          {cancelling === r.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                          Cancel return
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
    </ShopChrome>
  );
};

export default ShopReturns;
